import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CustomAlert, CustomTextField } from 'components/shared';
import { Field } from 'formik';
import React, { useState } from 'react';
import { Module } from 'types/permission';

interface PermissionFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  onGroupChange: (groupUuid: string) => void;
  duplicateError: string | null;
  clearDuplicateError: () => void;
  setFieldValue: (field: string, value: any) => void;
  groupsOption: { uuid: string; name: string }[];
  profilesGroup: { uuid: string; name: string }[];
  modules: Module[];
}

const permissionTypes = [
  { label: 'Visualizar', name: 'read' },
  { label: 'Listar', name: 'list' },
  { label: 'Criar', name: 'create' },
  { label: 'Editar', name: 'update' },
  { label: 'Deletar', name: 'delete' },
];

interface PermissionModuleProps {
  module: Module;
  values: any;
  setFieldValue: (field: string, value: any) => void;
}

const PermissionModule: React.FC<PermissionModuleProps> = ({
  module,
  values,
  setFieldValue,
}) => {
  const [open, setOpen] = useState(false);

  const key = module.name;
  const current = values.permissions?.[key] ?? {}; // <<< removido EMPTY

  // há pelo menos 1 permissão marcada?
  const hasAnyPermission = permissionTypes.some((t) => !!current[t.name]);
  const isActive = !!current.is_active;

  // estado do modal
  const [tempPermissions, setTempPermissions] = useState<Record<string, boolean>>(
    permissionTypes.reduce((acc, t) => {
      acc[t.name] = !!current[t.name];
      return acc;
    }, {} as Record<string, boolean>)
  );

  const handleOpen = () => {
    setTempPermissions(
      permissionTypes.reduce((acc, t) => {
        acc[t.name] = !!current[t.name];
        return acc;
      }, {} as Record<string, boolean>)
    );
    setOpen(true);
  };

  const handleSave = () => {
    // grava granulares
    permissionTypes.forEach((t) => {
      setFieldValue(`permissions.${key}.${t.name}`, !!tempPermissions[t.name]);
    });

    // ativa se houver pelo menos 1 marcada; senão, mantém inativo
    const anyChecked = permissionTypes.some((t) => !!tempPermissions[t.name]);
    setFieldValue(`permissions.${key}.is_active`, anyChecked);

    setOpen(false);
  };

  const toggleActive = (checked: boolean) => {
    if (checked) {
      if (!hasAnyPermission) {
        // não permite ativar sem nenhuma permissão; abre o modal
        handleOpen();
        return;
      }
      setFieldValue(`permissions.${key}.is_active`, true);
    } else {
      setFieldValue(`permissions.${key}.is_active`, false);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            {module.description}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Switch
              checked={isActive}
              onChange={(e) => toggleActive(e.target.checked)}
              color="primary"
            />
            <IconButton size="small" onClick={handleOpen}>
              <EditIcon fontSize="small" color={isActive ? 'primary' : 'action'} />
            </IconButton>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          Permissões:
          {permissionTypes.map((t) => {
            const checked = !!current[t.name];
            return (
              <Box
                key={t.name}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: checked ? '#e9f2ea' : '#f5f5f5',
                  fontSize: 11,
                  userSelect: 'none',
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Permissões: {module.description}</DialogTitle>
        <DialogContent>
          <FormGroup>
            {permissionTypes.map((t) => (
              <FormControlLabel
                key={t.name}
                control={
                  <Checkbox
                    checked={!!tempPermissions[t.name]}
                    onChange={(e) =>
                      setTempPermissions({ ...tempPermissions, [t.name]: e.target.checked })
                    }
                  />
                }
                label={t.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


const PermissionForm: React.FC<PermissionFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  duplicateError,
  clearDuplicateError,
  setFieldValue,
  groupsOption,
  profilesGroup,
  modules,
  onGroupChange
}) => {
  return (
    <Box sx={{ width: '100%', maxWidth: '1152px', mx: 'auto' }}>
      <Accordion defaultExpanded sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Dados do perfil</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={3}>
            {duplicateError && <CustomAlert severity="error" message={duplicateError} />}

            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={2}
              alignItems="flex-start"
            >
              <Box width={{ xs: '100%', sm: '50%' }}>
                <Field
                  name="name"
                  as={CustomTextField}
                  label="Nome"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={values.name.toUpperCase()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box width={{ xs: '100%', sm: '50%' }}>
                <FormControl fullWidth size="small" error={touched.groupUuid && Boolean(errors.groupUuid)}>
                  <InputLabel>Grupo</InputLabel>
                  <Field name="groupUuid">
                    {({ field }: any) => (
                      <Select
                        {...field}
                        label="Grupo"
                        value={values.groupUuid || ''}
                        onChange={(e) => {
                          field.onChange(e);
                          clearDuplicateError();
                          const groupUuid = e.target.value as string;
                          onGroupChange(groupUuid)
                        }}
                        onBlur={handleBlur}
                      >
                        {groupsOption.map((group) => (
                          <MenuItem key={group.uuid} value={group.uuid}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Field>
                  {touched.groupUuid && errors.groupUuid && (
                    <Typography variant="caption" color="error" mt={1}>
                      {errors.groupUuid}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>

            {values.groupUuid &&
              (<Box width={{ xs: '100%', sm: '50%' }}>
                <FormControl fullWidth size="small" error={touched.profileUuid && Boolean(errors.profileUuid)}>
                  <InputLabel>Subgrupo</InputLabel>
                  <Field name="profileUuid">
                    {({ field }: any) => (
                      <Select
                        {...field}
                        label="Subgrupo"
                        value={values.profileUuid || ''}
                        onChange={(e) => {
                          field.onChange(e);
                          clearDuplicateError();
                        }}
                        onBlur={handleBlur}
                      >
                        {profilesGroup.map((profile) => (
                          <MenuItem key={profile.uuid} value={profile.uuid}>
                            {profile.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Field>
                  {touched.profileUuid && errors.profileUuid && (
                    <Typography variant="caption" color="error" mt={1}>
                      {errors.profileUuid}
                    </Typography>
                  )}
                </FormControl>
              </Box>)
            }
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Configuração de Módulos e Permissões</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" mb={3}>
            Selecione as permissões que deseja conceder para cada módulo do sistema.
          </Typography>

          {modules.map((module) => (
            <PermissionModule
              key={module.name}
              module={module}
              values={values}
              setFieldValue={setFieldValue}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PermissionForm;
